//M10-15065
import moment from "moment";
import { AlertTriangle } from "lucide-react";

// M10-15065: shared "Rejection Details" panel used by both the Entry
// (InvoiceHomeDetailCard) and Approval (InvoiceApprovalDetailCard) list cards,
// so the styling/field logic lives in one place instead of two copies.
const formatRejectionCode = (code?: string) =>
  code ? code.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

const formatKey = (key: string) =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isIsoDateString = (value: any) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value) && !isNaN(new Date(value).getTime());

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  if (isIsoDateString(value)) {
    const d = new Date(value);
    return `${d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })}, ${d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`;
  }
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const labelOverrides: Record<string, string> = {
  rejection_remark: "Rejection Remarks",
  sub_remarks: "Rejection Sub Remarks",
  rejected_at: "Rejected Date",
};
const orderedKeys = [
  "rejection_header",
  "rejection_remark",
  "sub_remarks",
  "rejected_by_name",
  "rejected_at",
];
// Fields intentionally excluded from display (rejected_by/rejected_by_role are
// internal-only; corrective_hint/invoice_number/invoice_date were dropped per M10-15065).
const excludedKeys = new Set(["rejected_by", "rejected_by_role", "corrective_hint", "invoice_number", "invoice_date"]);

// M10-16197: Removed (7) / FUID Recalled (34) invoices also carry rejection details.
export const shouldShowRejectionDetails = (invoiceData: any) =>
  invoiceData?.is_rejection_pending == true ||
  [2, 4, 7, 34].includes(invoiceData?.fileStatusHdr?.fuid_status_id);

interface RejectionDetailsCardProps {
  invoiceData: any;
}

const RejectionDetailsCard = ({ invoiceData }: RejectionDetailsCardProps) => {
  if (!invoiceData?.rejection_code) return null;

  // Generic key/value rendering for rejection_data_email_desc — any new field
  // (e.g. product_code, product_name, mrp) shows up without a code change.
  const combinedData: Record<string, any> = { ...(invoiceData?.rejection_data_email_desc || {}) };
  const extraKeys = Object.keys(combinedData).filter(
    (key) => !orderedKeys.includes(key) && !excludedKeys.has(key)
  );

  const entries = [...orderedKeys, ...extraKeys]
    .map((key): [string, any] => [key, combinedData[key]])
    .filter(([, v]) => v !== null && v !== undefined && v !== "");

  return (
    <div className="mx-5 mb-5 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] overflow-hidden">
      <div className="px-5 py-3 bg-[#FEE2E2] border-b border-[#FCA5A5] flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-[#7F1D1D]" aria-hidden="true" />
        <h3 className="text-[#7F1D1D] font-semibold text-base">Rejection Details</h3>
      </div>

      <div className="px-5 py-4 space-y-4">
        <span className="inline-block text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full bg-[#FEE2E2] text-[#9B2C2C] border border-[#FCA5A5]">
          {formatRejectionCode(invoiceData?.rejection_code)}
        </span>

        {entries.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm pt-2 border-t border-[#FCA5A5]/40">
            {entries.map(([key, value]) => {
              const formatted = formatValue(value);
              const isLong = formatted.length > 60 || /\n/.test(formatted);
              return (
                <div key={key} className={`flex flex-col ${isLong ? "sm:col-span-2" : ""}`}>
                  <p className="text-xs uppercase tracking-wide text-[#9B2C2C] font-semibold mb-1">
                    {labelOverrides[key] || formatKey(key)}
                  </p>
                  <span className="text-[#303030] font-medium leading-relaxed whitespace-pre-wrap break-words">
                    {formatted}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {(invoiceData?.rejection_review_action_by || invoiceData?.rejection_review_action_date) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-xs pt-3 border-t border-dashed border-[#FCA5A5]/40 text-[#8A8A8A]">
            <div className="flex items-start gap-2">
              <p className="min-w-[110px]">Reviewed By</p>
              <span>{invoiceData?.rejection_review_action_by || "—"}</span>
            </div>
            <div className="flex items-start gap-2">
              <p className="min-w-[110px]">Reviewed On</p>
              <span>
                {invoiceData?.rejection_review_action_date
                  ? moment(invoiceData.rejection_review_action_date).format("MMMM Do YYYY, hh:mm A")
                  : "—"}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RejectionDetailsCard;
//M10-15065